import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  WarningCircle,
  Clock,
  CaretRight,
} from "@phosphor-icons/react";
import {
  getFamilyComplianceSummary,
  deriveComplianceStatus,
} from "@/lib/documents";

/**
 * Parent · Compliance score card for the dashboard.
 *
 * One aggregated view across family + per-child required documents.
 * Shows a single status chip (All set / Expiring soon / Action needed),
 * percent-complete bar, and a deep-link into /parent/documents that
 * scrolls to the first row that needs attention (via ?focus=…).
 *
 * Designed to be lightweight — fails silently to null (no card) if the
 * parent isn't linked to a family yet, or if the fetch fails. The
 * compliance surface itself is still reachable via the nav tab.
 */
export default function ComplianceCard({ profileId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    (async () => {
      try {
        const s = await getFamilyComplianceSummary(profileId);
        if (alive) setSummary(s);
      } catch {
        if (alive) setSummary(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [profileId]);

  if (loading || !summary) return null;
  if (summary.totalRequired === 0) return null;

  const status = deriveComplianceStatus(summary);
  const tone = toneStyles(status.tone);
  const Icon = toneIcon(status.tone);
  const href = status.focus
    ? `/parent/documents?focus=${status.focus}`
    : "/parent/documents";

  return (
    <Link
      to={href}
      data-testid="parent-compliance-card"
      data-tone={status.tone}
      data-focus={status.focus || ""}
      className={`block rounded-2xl border ${tone.border} bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone.iconBg}`}
        >
          <Icon size={22} weight="duotone" className={tone.iconColor} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Compliance
            </p>
            <span
              data-testid="parent-compliance-status-chip"
              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone.chip}`}
            >
              {status.label}
            </span>
          </div>
          <p className="mt-1 font-heading text-lg font-semibold tracking-tight text-neutral-900">
            {status.hint}
          </p>
          <p
            data-testid="parent-compliance-count-line"
            className="mt-0.5 text-xs text-neutral-500"
          >
            <span data-testid="parent-compliance-approved">
              {summary.approvedCount}
            </span>
            /{summary.totalRequired} complete
            {summary.missingRequired > 0 && (
              <>
                {" · "}
                <span
                  data-testid="parent-compliance-missing"
                  className="font-medium text-[#C6851E]"
                >
                  {summary.missingRequired} missing
                </span>
              </>
            )}
            {summary.expiringRequired > 0 && (
              <>
                {" · "}
                <span
                  data-testid="parent-compliance-expiring"
                  className="font-medium text-amber-700"
                >
                  {summary.expiringRequired} expiring
                </span>
              </>
            )}
            {summary.rejectedCount > 0 && (
              <>
                {" · "}
                <span
                  data-testid="parent-compliance-rejected"
                  className="font-medium text-[#C6851E]"
                >
                  {summary.rejectedCount} rejected
                </span>
              </>
            )}
            {summary.uploadedCount > 0 && (
              <>
                {" · "}
                <span
                  data-testid="parent-compliance-pending"
                  className="font-medium text-neutral-900"
                >
                  {summary.uploadedCount} in review
                </span>
              </>
            )}
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-neutral-100">
            <div
              data-testid="parent-compliance-progress"
              data-percent={summary.percentComplete}
              className={`h-full rounded-full transition-all ${tone.progress}`}
              style={{ width: `${summary.percentComplete}%` }}
            />
          </div>
        </div>
        <CaretRight
          size={16}
          weight="bold"
          className="shrink-0 self-center text-neutral-400"
        />
      </div>
    </Link>
  );
}

function toneStyles(tone) {
  switch (tone) {
    case "alert":
      return {
        border: "border-[#C6851E]/40",
        iconBg: "bg-[#C6851E]/10",
        iconColor: "text-[#C6851E]",
        chip: "bg-[#C6851E]/10 text-[#C6851E] border-[#C6851E]/40",
        progress: "bg-[#C6851E]",
      };
    case "warning":
      return {
        border: "border-amber-300/60",
        iconBg: "bg-amber-50",
        iconColor: "text-amber-700",
        chip: "bg-amber-50 text-amber-700 border-amber-200",
        progress: "bg-amber-400",
      };
    case "ok":
    default:
      return {
        border: "border-neutral-200",
        iconBg: "bg-primary/10",
        iconColor: "text-primary-hover",
        chip: "bg-primary/10 text-primary-hover border-primary/30",
        progress: "bg-primary",
      };
  }
}

function toneIcon(tone) {
  switch (tone) {
    case "alert":
      return WarningCircle;
    case "warning":
      return Clock;
    case "ok":
    default:
      return ShieldCheck;
  }
}
